/**
 * 깊이 추정 파사드.
 *
 * 백엔드가 둘이고 둘 다 같은 얼굴을 하고 있어야 한다 — 호출부는 어느 쪽이
 * 돌았는지 몰라도 된다. ONNX 쪽이 실패하면(오프라인, WebGPU 없음, CDN 차단)
 * 조용히 휴리스틱으로 내려앉는다. 앱이 멈추는 것보다 낫다.
 */

import type { DepthBackend, Field, ImageAnalysis } from '../types.ts';
import { makeField } from '../imaging/raster.ts';
import { heuristicDepth, shadingDepth } from './heuristic.ts';
import { createDepthWorker } from './onnxWorker.ts';

export interface DepthConfig {
  /** transformers.js를 어디서 받을지. 오프라인이면 자기 서버 경로로 바꾸면 된다. */
  cdn: string;
  modelId: string;
  /** 'fp16' | 'q8' | 'q4' | 'fp32' — 작을수록 빠르고 거칠다. */
  dtype: string;
  /** 'auto' | 'webgpu' | 'wasm' */
  device: string;
  /** 모델에 넣기 전에 이 크기로 줄인다. 518이 Depth Anything의 학습 해상도. */
  inferenceSize: number;
}

export const DEFAULT_DEPTH_CONFIG: DepthConfig = {
  cdn: 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@4.2.0',
  modelId: 'onnx-community/depth-anything-v2-small',
  dtype: 'fp16',
  device: 'auto',
  inferenceSize: 518,
};

export interface DepthProgress {
  phase: string;
  file?: string;
  ratio?: number;
}

export interface EstimateOptions {
  backend: DepthBackend;
  /**
   * 깎기용: 휴리스틱은 셰이딩만(거리장 돔 없이), 신경망은 부피 혼합 없이 모델 출력 그대로.
   * 단일 뷰 부풀리기용 깊이(기본)는 둘 다 돔을 섞는다 — 종잇장이 되지 않게.
   */
  forCarve?: boolean;
  config?: Partial<DepthConfig>;
  onProgress?: (p: DepthProgress) => void;
  signal?: AbortSignal;
}

export interface DepthOutcome {
  field: Field;
  backend: DepthBackend;
  device?: string;
  ms: number;
  /** ONNX를 요청했는데 못 써서 내려앉았다면 사유가 들어온다. */
  fallbackReason?: string;
}

let worker: Worker | null = null;

function getWorker(): Worker {
  if (!worker) worker = createDepthWorker();
  return worker;
}

/** 워커가 이상해졌을 때 버린다. 모델 캐시도 같이 날아간다. */
export function resetDepthWorker(): void {
  worker?.terminate();
  worker = null;
}

function rasterToRgbaBuffer(analysis: ImageAnalysis): ArrayBuffer {
  const src = analysis.source.data;
  const copy = new Uint8ClampedArray(src.length);
  copy.set(src);
  return copy.buffer;
}

function runHeuristic(analysis: ImageAnalysis): DepthOutcome {
  const t0 = performance.now();
  const field = heuristicDepth(analysis.mask, analysis.distance, analysis.luma);
  return { field, backend: 'heuristic', ms: performance.now() - t0 };
}

async function runOnnx(
  analysis: ImageAnalysis,
  config: DepthConfig,
  onProgress?: (p: DepthProgress) => void,
  signal?: AbortSignal,
): Promise<DepthOutcome> {
  const w = getWorker();
  const { width, height } = analysis.source;

  return new Promise<DepthOutcome>((resolve, reject) => {
    const cleanup = () => {
      w.removeEventListener('message', onMessage);
      w.removeEventListener('error', onError);
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => { cleanup(); reject(new Error('취소되었습니다.')); };
    const onError = (e: ErrorEvent) => { cleanup(); reject(new Error(e.message || '워커 오류')); };

    const onMessage = (event: MessageEvent) => {
      const msg = event.data;
      if (msg.type === 'progress') { onProgress?.(msg); return; }
      cleanup();
      if (msg.type === 'error') { reject(new Error(msg.message)); return; }
      if (msg.type === 'result') {
        const field: Field = {
          width: msg.width,
          height: msg.height,
          data: new Float32Array(msg.buffer),
        };
        resolve({ field, backend: 'onnx', device: msg.device, ms: msg.ms });
      }
    };

    w.addEventListener('message', onMessage);
    w.addEventListener('error', onError);
    signal?.addEventListener('abort', onAbort, { once: true });

    const buffer = rasterToRgbaBuffer(analysis);
    w.postMessage({ ...config, width, height, buffer }, [buffer]);
  });
}

/**
 * 깊이맵을 만든다. 결과는 항상 원본 해상도, 0..1, 마스크 바깥은 0.
 * ONNX 결과에도 마스크를 곱한다 — 모델은 배경을 모르고, 배경까지 밀어내면
 * 메시가 액자처럼 나온다.
 */
export async function estimateDepth(
  analysis: ImageAnalysis,
  opts: EstimateOptions,
): Promise<DepthOutcome> {
  const config = { ...DEFAULT_DEPTH_CONFIG, ...opts.config };

  const heuristic = (): DepthOutcome => {
    if (!opts.forCarve) return runHeuristic(analysis);
    const t0 = performance.now();
    return { field: shadingDepth(analysis.mask, analysis.luma), backend: 'heuristic', ms: performance.now() - t0 };
  };

  if (opts.backend === 'heuristic') {
    return applyMask(heuristic(), analysis);
  }

  try {
    const outcome = await runOnnx(analysis, config, opts.onProgress, opts.signal);
    // 모델 결과와 부피 단서를 섞는다. 순수 모델 출력은 캐릭터가 종잇장처럼 나온다.
    const blended = opts.forCarve ? outcome.field : blendWithVolume(outcome.field, analysis, 0.72);
    return applyMask({ ...outcome, field: blended }, analysis);
  } catch (err) {
    resetDepthWorker();
    const fallback = applyMask(heuristic(), analysis);
    fallback.fallbackReason = err instanceof Error ? err.message : String(err);
    return fallback;
  }
}

function applyMask(outcome: DepthOutcome, analysis: ImageAnalysis): DepthOutcome {
  const { mask } = analysis;
  const out = makeField(outcome.field.width, outcome.field.height);
  for (let i = 0; i < out.data.length; i++) {
    out.data[i] = outcome.field.data[i]! * (mask.data[i] ?? 1);
  }
  return { ...outcome, field: out };
}

/** 모델 깊이 × 실루엣 부피. 앞은 모델이, 두께는 거리장이 책임진다. */
function blendWithVolume(depth: Field, analysis: ImageAnalysis, modelWeight: number): Field {
  const { distance } = analysis;
  const out = makeField(depth.width, depth.height);
  for (let i = 0; i < out.data.length; i++) {
    const volume = Math.pow(distance.data[i] ?? 0, 0.55);
    out.data[i] = depth.data[i]! * modelWeight + volume * (1 - modelWeight);
  }
  return out;
}
